import { Injectable } from '@angular/core'
import { BehaviorSubject, Subject } from 'rxjs'
import { PlatformService } from '../platforms/platform.service'
import { LogService } from 'htv-lib'

@Injectable({
  providedIn: 'root',
})
export class VolumeService {
  muted!: boolean
  volume!: number
  currentMuted$ = new Subject<boolean>()
  currentVolume$ = new Subject<number>()
  useNativeVolumeDisplay$ = new BehaviorSubject<boolean>(false)

  readonly VOLUME_DEFAULT = 5
  readonly VOLUME_MAX = 50

  constructor(
    private logService: LogService,
    private platformService: PlatformService
  ) {
    // do nothing
  }

  init(): void {
    this.logService.info(`[VolumeService] Init`)
    this.setVolume(this.VOLUME_DEFAULT)
    this.setVolumeMute(false)
  }

  setNativeVolumeDisplay(useNativeVolumeDisplay = false): void {
    if (this.useNativeVolumeDisplay$.value !== useNativeVolumeDisplay) {
      this.useNativeVolumeDisplay$.next(useNativeVolumeDisplay)
      this.platformService.setNativeVolumeDisplay(useNativeVolumeDisplay)
    }
  }

  async refresh(): Promise<void> {
    try {
      this.muted = await this.platformService.getVolumeMute()
      this.volume = await this.platformService.getVolume()
      this.currentMuted$.next(this.muted)
      this.currentVolume$.next(this.volume)
    } catch {
      this.logService.info(`[VolumeService] Could not refresh volume`)
    }
  }

  async setVolume(volume: number): Promise<void> {
    if (volume > this.VOLUME_MAX) {
      volume = this.VOLUME_MAX
    }
    try {
      await this.platformService.setVolume(volume)
    } catch {
      this.logService.info(`[VolumeService] Could not set volume to ${volume}`)
    }
    await this.refresh()
  }

  async setVolumeDown(): Promise<void> {
    if (this.volume > 0) {
      await this.platformService.setVolumeDown()
    }
    this.refresh()
  }

  async setVolumeUp(): Promise<void> {
    if (this.volume < this.VOLUME_MAX) {
      await this.platformService.setVolumeUp()
    }
    this.refresh()
  }

  switchVolumeMute(): void {
    this.setVolumeMute(!this.muted)
  }

  async setVolumeMute(muted: boolean): Promise<void> {
    try {
      await this.platformService.setVolumeMute(muted)
    } catch {
      this.logService.info(`[VolumeService] Could not set mute to ${muted}`)
    }
    await this.refresh()
  }
}
